'use client';

import { cn } from '../../../../lib/utils';
import { useEffect, useMemo, useState } from 'react';
import {
  ColorFormat,
  ColorPickerMode,
  ColorPickerProvider,
} from './color-picker-context';
import { ColorPickerArea } from './color-picker-area';
import { ColorPickerGradientArea } from './color-picker-gradient-area';
import { ColorPickerInput } from './color-picker-input';
import { ColorPickerModeSwitch } from './color-picker-mode-switch';
import { ColorPickerPresets } from './ColorPickerPresets';

export interface ColorPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** 드래그 종료·프리셋 클릭·입력 확정 등 인터랙션이 완료된 시점에 호출 */
  onChangeEnd?: (value: string | null) => void;
  mode?: ColorPickerMode;
  onModeChange?: (mode: ColorPickerMode) => void;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
  /** opacity 인터랙션 완료 시점에 호출 */
  onOpacityChangeEnd?: (opacity: number) => void;
  className?: string;
  children: React.ReactNode;
}

function BaseColorPicker({
  value,
  onChange,
  onChangeEnd,
  mode = 'solid',
  onModeChange,
  opacity,
  onOpacityChange,
  onOpacityChangeEnd,
  className,
  children,
}: ColorPickerProps) {
  const [lastSolidColor, setLastSolidColor] = useState('#FFFFFF');
  const [internalOpacity, setInternalOpacity] = useState(1);
  const [format, setFormat] = useState<ColorFormat>('hex');

  useEffect(() => {
    if (!value) {
      return;
    }
    if (value.startsWith('#')) {
      const normalized = value.toUpperCase();
      setLastSolidColor(normalized);
    }
  }, [value]);

  const resolvedOpacity = opacity ?? internalOpacity;
  const resolvedOnOpacityChange = onOpacityChange ?? setInternalOpacity;

  const contextValue = useMemo(
    () => ({
      mode,
      onModeChange,
      value,
      onChange,
      onChangeEnd,
      isNone: value == null,
      opacity: resolvedOpacity,
      onOpacityChange: resolvedOnOpacityChange,
      onOpacityChangeEnd,
      format,
      onFormatChange: setFormat,
      lastSolidColor,
      setLastSolidColor,
    }),
    [
      mode,
      onModeChange,
      value,
      onChange,
      onChangeEnd,
      resolvedOpacity,
      resolvedOnOpacityChange,
      onOpacityChangeEnd,
      format,
      lastSolidColor,
    ],
  );

  return (
    <ColorPickerProvider value={contextValue}>
      <div className={cn('flex w-full flex-col gap-sm', className)}>
        {children}
      </div>
    </ColorPickerProvider>
  );
}

const ColorPicker = Object.assign(BaseColorPicker, {
  Area: ColorPickerArea,
  GradientArea: ColorPickerGradientArea,
  Input: ColorPickerInput,
  ModeSwitch: ColorPickerModeSwitch,
  Presets: ColorPickerPresets,
});

export { ColorPicker };
